import type {
  CalculatedMeasure,
  ColumnRole,
  DatasetCapabilities,
  DatasetColumn,
  DatasetDescriptionInput,
  DatasetFixedFilter,
  DatasetLookup,
  DatasetProfile,
  ProfileColumn
} from 'src/components/star-grid/datasets/datasetTypes';
import { effectiveHierarchyChoice, type HierarchyChoice } from 'src/components/star-grid/datasets/utils/datasetHierarchy';
import { savedCapabilities } from 'src/components/star-grid/datasets/utils/datasetCapabilities';

/** Per-column overrides the wizard collects on top of the profiler's suggestions. */
export interface DraftColumnChoices {
  /** column name -> confirmed role (falls back to the profiler's suggestion). */
  readonly roles: Readonly<Record<string, ColumnRole>>;
  /** dimension column name -> display label. */
  readonly labels: Readonly<Record<string, string>>;
  /** dimension column name -> confirmed hierarchy choice (falls back to the top candidate, else flat). */
  readonly hierarchyChoices: Readonly<Record<string, HierarchyChoice>>;
  /** The dimension whose members are the measures, when the dataset has a single value column. */
  readonly measureDimension?: string | null;
}

/** The role the user picked for a column, else the profiler's suggestion. */
export const effectiveRole = (column: ProfileColumn, roles: Readonly<Record<string, ColumnRole>>): ColumnRole =>
  roles[column.name] ?? column.suggested_role;

function toDatasetColumn(column: ProfileColumn, role: ColumnRole, choices: DraftColumnChoices): DatasetColumn {
  const label = choices.labels[column.name]?.trim();
  if (role === 'measure') return { name: column.name, role, aggregation: 'sum', ...(label ? { label } : {}) };
  // Without its format a date or `YYYY-MM` time column is read as `yyyymm` and matches no period.
  if (role === 'time') return { name: column.name, role, ...(column.time_format ? { time_format: column.time_format } : {}) };
  if (role !== 'dimension') return { name: column.name, role };
  const choice = effectiveHierarchyChoice(column, choices.hierarchyChoices);
  return {
    name: column.name,
    role,
    ...(label ? { label } : {}),
    ...(choices.measureDimension === column.name ? { measure_dimension: true } : {}),
    hierarchy: choice.kind === 'hierarchy' ? { dimension: choice.dimension, hierarchy: choice.hierarchy } : null
  };
}

/** A calculated measure as stored; the service parses and checks its formula. */
export function calculatedColumn(measure: CalculatedMeasure): DatasetColumn {
  return {
    name: measure.name.trim(),
    role: 'measure',
    formula: measure.formula.trim(),
    format: measure.format,
    ...(measure.per_row ? { per_row: true } : {})
  };
}

/** Turns the profiled columns plus the user's choices into stored dataset columns, dropping `ignore`. */
export function buildDatasetColumns(
  profile: DatasetProfile,
  choices: DraftColumnChoices,
  calculated: readonly CalculatedMeasure[] = []
): DatasetColumn[] {
  const kept = profile.columns.map((column) => ({ column, role: effectiveRole(column, choices.roles) })).filter(({ role }) => role !== 'ignore');
  // A measure dimension names which measure a row reads, so it only holds while there is exactly one measure.
  const formulas = calculated.filter((measure) => measure.name.trim() && measure.formula.trim()).map(calculatedColumn);
  const single = kept.filter(({ role }) => role === 'measure').length === 1 && formulas.length === 0;
  const effective = single ? choices : { ...choices, measureDimension: null };
  return [...kept.map(({ column, role }) => toDatasetColumn(column, role, effective)), ...formulas];
}

/** A fixed filter as stored: values trimmed, and blanks dropped except `between`'s positional pair. */
export function savedFixedFilter(filter: DatasetFixedFilter): DatasetFixedFilter {
  const values = filter.values.map((value) => value.trim());
  return { ...filter, values: filter.op === 'between' ? values : values.filter(Boolean) };
}

/** Assembles the create body posted to `POST /datasets`. Every dataset is global until private and shared ones ship. */
export function buildDatasetDescription(
  name: string,
  profile: DatasetProfile,
  choices: DraftColumnChoices,
  lookups: readonly DatasetLookup[],
  fixedFilters: readonly DatasetFixedFilter[] = [],
  capabilities: DatasetCapabilities = {},
  calculated: readonly CalculatedMeasure[] = []
): DatasetDescriptionInput {
  const columns = buildDatasetColumns(profile, choices, calculated);
  return {
    name: name.trim(),
    ref: profile.ref,
    columns,
    lookups: [...lookups],
    fixed_filters: fixedFilters.map(savedFixedFilter),
    visibility: 'global',
    capabilities: savedCapabilities(capabilities, columns)
  };
}
